import React, { useState, useEffect, useRef } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import ApplyNow from "./ApplyNow";
import StatusModal from "./StatusModal";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    consent: true,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    type: 'success' | 'error';
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_ACCESS_KEY,
          subject: "New Contact Form Submission",
          sender_name: formData.fullName,
          sender_email: formData.email,
          consent: formData.consent ? "Accepted" : "Not Accepted",
          message: formData.message,
          phone: formData.phone,
          reply_to: formData.email,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setModalConfig({
          isOpen: true,
          type: 'success',
          title: 'Message Sent!',
          message: "Our team has received your message and will get back to you shortly.",
        });
        setFormData({ fullName: "", email: "", consent: true, phone: "", message: "" });
      } else {
        setModalConfig({
          isOpen: true,
          type: 'error',
          title: 'Transmission Failed',
          message: 'Failed to send message. Please try again later.',
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setModalConfig({
        isOpen: true,
        type: 'error',
        title: 'Network Error',
        message: 'A network error occurred. Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(headerRef.current, {
        y: 40, opacity: 0, duration: 1, ease: "power4.out", delay: 0.2, clearProps: "all",
      });
      gsap.from(leftRef.current, {
        x: -60, opacity: 0, duration: 1.2, ease: "power4.out", delay: 0.4, clearProps: "all",
      });
      gsap.from(rightRef.current, {
        x: 60, opacity: 0, duration: 1.2, ease: "power4.out", delay: 0.4, clearProps: "all",
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  const inputClass = "w-full px-5 py-4 rounded-[16px] bg-white/60 backdrop-blur-md text-[var(--text-primary)] font-bold placeholder:text-[var(--text-gray-light)] border-2 border-white/50 focus:outline-none focus:border-[var(--primary)] focus:bg-white/80 transition-all duration-300 text-base";

  return (
    <div ref={containerRef} className="min-w-screen bg-[var(--background-primary)] text-[var(--text-primary)]">
      <div className="max-w-[1440px] mx-auto flex flex-col gap-12 px-4 sm:px-6 lg:px-12 py-16">
        <Navbar />

        {/* Header */}
        <div ref={headerRef} className="text-center mt-14">
          <h1 className="text-4xl md:text-7xl font-[900] leading-none tracking-tighter mb-6 bg-gradient-to-r from-[var(--primary)] to-[var(--secondary-accent)] text-transparent bg-clip-text">
            Contact Us
          </h1>
          <p className="text-[var(--text-gray)] font-bold text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            We're here to help! Whether you have questions about your loan, our services, or anything else — we'll respond quickly and personally.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* LEFT SIDE – Contact Info */}
          <div ref={leftRef} className="flex flex-col gap-6">

            {/* Info Card */}
            <div className="bg-white/40 backdrop-blur-3xl rounded-[32px] md:rounded-[40px] p-8 border-2 border-white/50 shadow-[20px_20px_40px_rgba(0,0,0,0.03)]">
              <p className="text-[var(--text-gray)] font-bold text-lg leading-relaxed mb-8">
                Your satisfaction is our top priority. Reach out anytime and our support team will get back to you as soon as possible.
              </p>
              <div className="space-y-6">
                {[
                  { emoji: "📞", label: "Call Center", value: import.meta.env.VITE_COMPANY_PHONE, href: `tel:${import.meta.env.VITE_COMPANY_PHONE}` },
                  { emoji: "📧", label: "Email", value: import.meta.env.VITE_COMPANY_EMAIL, href: `mailto:${import.meta.env.VITE_COMPANY_EMAIL}` },
                  { emoji: "📍", label: "Location", value: import.meta.env.VITE_COMPANY_ADDRESS, href: undefined },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-[14px] bg-white border border-[var(--border-light)] flex items-center justify-center text-lg shadow-sm shrink-0">
                      {item.emoji}
                    </div>
                    <div>
                      <div className="text-[9px] font-black text-[var(--text-gray-light)] uppercase tracking-[0.2em] mb-1">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-[var(--text-primary)] font-black hover:text-[var(--primary)] transition-colors text-sm leading-snug">{item.value}</a>
                      ) : (
                        <span className="text-[var(--text-primary)] font-black text-sm leading-snug">{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>



            {/* Google Map */}
            <div className="rounded-[40px] overflow-hidden border-2 border-white/50 shadow-2xl">
              <iframe
                title={import.meta.env.VITE_COMPANY_NAME}
                src={import.meta.env.VITE_COMPANY_ADDRESS_MAP_URL}
                width="100%"
                height="280"
                className="border-0 block"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>

          {/* RIGHT SIDE – Contact Form */}
          <form
            ref={rightRef}
            onSubmit={handleSubmit}
            className="bg-white/40 backdrop-blur-3xl rounded-[32px] md:rounded-[40px] p-8 md:p-12 border-2 border-white/50 shadow-[20px_20px_40px_rgba(0,0,0,0.03)] flex flex-col gap-5 relative overflow-hidden"
          >
            <div className="absolute top-[-10%] right-[-10%] w-48 h-48 bg-[var(--primary)] opacity-[0.04] rounded-full blur-[80px] pointer-events-none" />

            <div className="mb-2">
              <h2 className="text-3xl font-[900] text-[var(--text-primary)] leading-none tracking-tighter mb-2">
                Get in Touch
              </h2>
              <p className="text-[var(--text-gray)] font-bold text-sm">Fill the form below and we'll reach out shortly.</p>
            </div>

            <input type="text" name="fullName" placeholder="Full Name *" value={formData.fullName} onChange={handleChange} required className={inputClass} />
            <input type="email" name="email" placeholder="Email Address *" value={formData.email} onChange={handleChange} required className={inputClass} />
            <input type="tel" name="phone" placeholder="Phone Number *" value={formData.phone} onChange={handleChange} required className={inputClass} />
            <textarea name="message" placeholder="Your Message" value={formData.message} onChange={handleChange} rows={4} className={inputClass} />

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="consent"
                checked={formData.consent}
                onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                className="mt-0.5 h-4 w-4 accent-[var(--primary)] cursor-pointer shrink-0"
              />
              <label className="text-xs text-[var(--text-gray)] font-bold leading-relaxed">
                I consent to receive communications via RCS, WhatsApp, email, or SMS.
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-5 rounded-[20px] font-black text-white text-base bg-[var(--text-primary)] shadow-[0_20px_40px_rgba(0,0,0,0.1)] hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-60 uppercase tracking-widest"
            >
              {loading ? "Sending…" : "Submit Message →"}
            </button>
          </form>
        </div>

      </div>
      <ApplyNow />
      <Footer />

      <StatusModal
        isOpen={modalConfig.isOpen}
        onClose={() => setModalConfig({ ...modalConfig, isOpen: false })}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
      />
    </div>
  );
};

export default ContactUs;
